<template>
  <Report
    :report-id="reportId"
    :title="title"
    class="chart-container"
    :data-empty="isDataEmpty"
    :loaded-data="isDataLoaded"
    :loading-error="loadingError"
    no-labels
  >
    <template #chart>
      <div class="doughnut-chart-container">
        <DoughnutChartBaseWrapper
          :title="subTitle"
          class="chart"
          :legend-element-id="'legendContainer-' + reportId"
          :labels-and-data="processedData"
          :dataFilter="dataFilter"
          :series="series"
          :max-items="maxItems"
        />
      </div>
      <div :id="'legendContainer-' + reportId" class="legend-container"></div>
    </template>
  </Report>
</template>

<script lang="ts" setup>
import DoughnutChartBaseWrapper from '../chartjs/DoughnutChartBaseWrapper.vue';
import { Data, DataFilter, LabelsAndData, Series } from '../../models';
import { chartDataUtil } from '../../util/ChartDataUtil';
import { useReportData } from '@/features/reports/util/useReportData';
import { computed, PropType } from 'vue';
import Report from '@/features/reports/components/Report.vue';

const props = defineProps({
  data: {
    type: Object as PropType<Data>,
    required: true,
  },
  loadedData: {
    type: Boolean,
    default: false,
  },
  loadingError: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  subTitle: {
    type: String,
    default: '',
  },
  dataFilter: {
    type: Object as PropType<DataFilter>,
  },
  series: {
    type: [Function, String] as PropType<((data: Data) => Series) | string>,
    required: true,
  },
  maxItems: {
    type: Number,
    default: 10,
  },
});

const { reportId, isDataLoaded, isDataEmpty } = useReportData(props);

const processedData = computed<LabelsAndData>(() => {
  const labelsAndData = { labels: props.data.scale.labels, data: seriesData.value.data };

  const filter = (label: string, value: number) => value > 0;
  const filteredData = chartDataUtil.filterData(labelsAndData, filter);

  const orderedData = chartDataUtil.orderData(filteredData, {
    dataOrderAscending: false,
    forceZeroValuesToBottom: false,
  });

  return chartDataUtil.restrictDataToTopX(orderedData, {
    maxItems: props.maxItems,
    groupNonDisplayedItemsIntoOther: true,
    otherItemsLabel: 'Others',
  });
});

const seriesData = computed<Series>(() => {
  if (typeof props.series === 'string') {
    return props.data.series.filter((series) => series.name === props.series)[0];
  } else {
    return props.series(props.data);
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '@/features/reports/components/reports.scss' as *;

.chart-content {
  flex: 1;
  display: flex;
  padding: 0px 6px;
}

.doughnut-chart-container {
  min-width: 237px;
  max-width: 237px;
  display: flex;
  align-items: center;
}

.legend-container {
  margin: auto;
  flex: 1;
  padding-left: 1rem;
}

:deep(.sk-folding-cube) {
  margin: 6rem auto;
}

.no-data,
.error-overlay {
  min-width: 237px;
}
</style>
